import React, { forwardRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskCheck } from '../TaskCheck'
import { SubtaskTreeLines, treeRowClasses } from './SubtaskTreeLines'
import type { TreeLines } from './subtaskTreeLayout'

interface OpenRowProps {
  /** Wie viele Zeilen die Liste gerade hat — woran `sent` erkennt, dass die neue dasteht. */
  itemCount: number
  onAdd: (title: string) => void
  /** Sie wurde leer verlassen: Steht sie mitten in der Liste, gehört sie zurück ans Ende. */
  onLeaveEmpty: () => void
  /** Mitten im Baum steht sie auf der Ebene der Zeile, hinter der sie entsteht (#181). */
  lines?: TreeLines
}

/**
 * Die **offene Zeile**: der Platz, an dem die nächste Unteraufgabe entsteht.
 *
 * **Sie ist noch keine Unteraufgabe.** Sie steht in keiner Tabelle, geht in
 * keinen Sync und hinterlässt nichts, wenn man sie leer verlässt — erst Enter
 * macht sie zu einer Zeile. Genau das sagt ihr gestrichelter Haken.
 *
 * Es gibt sie immer genau einmal: am Ende der Liste, oder dort, wo
 * Cmd/Ctrl+Enter sie eingeschoben hat.
 */
export const OpenRow = forwardRef<HTMLInputElement, OpenRowProps>(({ itemCount, onAdd, onLeaveEmpty, lines }, ref) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')

  /**
   * Wie viele Zeilen dastanden, als die letzte abgeschickt wurde — `null`, wenn
   * gerade nichts unterwegs ist.
   *
   * Die Liste schreibt in die Datenquelle und wartet auf deren Antwort. Das Feld
   * leerte sich sofort; dazwischen lag mindestens ein Bild, in dem das Getippte
   * nirgends stand — es verschwand und kam als Zeile zurück (gemeldet 2026-09-19).
   */
  const [sent, setSent] = useState<number | null>(null)
  // Die Zahl der Zeilen und nicht der Titel: Zwei gleich benannte Unteraufgaben
  // sind erlaubt, und die zweite ließe sich am Titel nicht von der ersten
  // unterscheiden.
  const arrived = sent !== null && itemCount > sent
  /*
    Im RENDER geleert, nicht in einem Effekt. Ein Effekt liefe nach dem Bild, in
    dem die Zeile erscheint — für genau ein Bild stünde der Text dann doppelt da,
    in der Zeile und im Feld darunter.
  */
  const draftValue = arrived ? '' : draft

  useEffect(() => {
    if (!arrived) return
    setSent(null)
    setDraft('')
  }, [arrived])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    // Never let Enter reach the card while this row is focused: it would save
    // and collapse in the middle of typing a list. Cmd/Ctrl+Enter genauso — die
    // Karte legte sonst eine neue AUFGABE unter der aktuellen an.
    e.preventDefault()
    e.stopPropagation()
    const trimmed = draftValue.trim()
    if (!trimmed) {
      e.currentTarget.blur()
      return
    }
    // Das Feld behält seinen Text, bis die Zeile wirklich dasteht (siehe `sent`).
    setSent(itemCount)
    onAdd(trimmed)
  }

  return (
    <div className={['subtask-row', 'subtask-row--open', ...treeRowClasses(lines)].join(' ')}>
      <SubtaskTreeLines lines={lines} />
      {/*
        **Immer gestrichelt** (Figma `5363:170471`, Nutzer-Vorgabe 2026-09-20):
        auch während hier getippt wird. Der Strich sagt „gehört noch nicht zur
        Liste darüber" — und das gilt bis zum Enter, nicht bis zum ersten
        Zeichen.
      */}
      <TaskCheck
        checked={false}
        onCheckedChange={() => {}}
        kind="subtask"
        placeholder
        disabled
      />
      <input
        ref={ref}
        className="subtask-row__title"
        type="text"
        placeholder={t('taskCreation.subtaskPlaceholder')}
        value={draftValue}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (!draftValue.trim()) onLeaveEmpty() }}
        spellCheck={false}
      />
    </div>
  )
})

OpenRow.displayName = 'OpenRow'
